import { useEffect, useMemo, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { scaleBand, scaleLinear } from "@visx/scale";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { AnimatedBar, ChartCard, Skeleton } from "@/components/reusable";
import { fetchYearBuiltDistribution } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { YearBuiltBucket } from "@/types/analytics";

const BAR_COLOURS: Record<string, string> = {
  "Pre-1990": "#E24B4A",
  "1990s": "#EF9F27",
  "2000s": "#85B7EB",
  "2010s": "#378ADD",
  "2020s": "#185FA5",
};

export function VesselsByYearBar() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<YearBuiltBucket[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchYearBuiltDistribution(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  return (
    <ChartCard title="Vessels by year built" subtitle="Age distribution" minHeight={320}>
      {!data ? (
        <Skeleton height="h-full" className="min-h-[180px]" />
      ) : (
        <div className="h-full w-full">
          <ParentSize>
            {({ width, height }) =>
              width > 0 && height > 0 ? (
                <YearChart data={data} width={width} height={height} />
              ) : null
            }
          </ParentSize>
        </div>
      )}
    </ChartCard>
  );
}

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid rgba(0,0,0,0.14)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
};

function YearChart({
  data,
  width,
  height,
}: {
  data: YearBuiltBucket[];
  width: number;
  height: number;
}) {
  const margin = { top: 8, right: 8, bottom: 24, left: 30 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);
  const max = Math.max(1, ...data.map((d) => d.count));

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        range: [0, innerW],
        domain: data.map((d) => d.label),
        padding: 0.35,
      }),
    [data, innerW]
  );
  const yScale = useMemo(
    () => scaleLinear<number>({ range: [innerH, 0], domain: [0, max * 1.1], nice: true }),
    [innerH, max]
  );

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<YearBuiltBucket>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left}>
          {data.map((d, i) => {
            const barWidth = xScale.bandwidth();
            const barHeight = innerH - (yScale(d.count) ?? 0);
            const barX = xScale(d.label) ?? 0;
            const barY = innerH - barHeight;
            return (
              <AnimatedBar
                key={d.label}
                x={barX}
                width={barWidth}
                targetY={barY}
                targetHeight={barHeight}
                baselineY={innerH}
                fill={BAR_COLOURS[d.label] ?? "var(--color-brand-primary)"}
                rx={3}
                delayMs={i * 60}
                durationMs={650}
                onMouseMove={(e) => {
                  const svg = e.currentTarget.ownerSVGElement as SVGSVGElement | null;
                  const pt = svg?.getBoundingClientRect();
                  showTooltip({
                    tooltipData: d,
                    tooltipLeft: pt ? e.clientX - pt.left : barX + barWidth / 2,
                    tooltipTop: pt ? e.clientY - pt.top : barY,
                  });
                }}
                onMouseLeave={hideTooltip}
              />
            );
          })}
          <AxisBottom
            top={innerH}
            scale={xScale}
            hideAxisLine
            tickStroke="transparent"
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "middle",
              fontFamily: "var(--font-sans)",
            })}
          />
          <AxisLeft
            scale={yScale}
            numTicks={4}
            hideAxisLine
            tickStroke="transparent"
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "end",
              dx: -4,
              dy: 3,
              fontFamily: "var(--font-sans)",
            })}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="font-medium">{tooltipData.label}</div>
          <div className="text-[var(--color-text-secondary)]">
            {tooltipData.count} vessel{tooltipData.count === 1 ? "" : "s"}
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
}
